import { useRef } from "react";
import {
  Box,
  Unstable_Grid2 as Grid,
  TextField,
  MenuItem,
  Switch,
  CircularProgress,
  Tooltip,
  Button,
  IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

type Schedule = {
  frequency: "daily" | "weekly" | "monthly";
  dayOfWeek: number[];
  monthsInterval?: number;
  startDate?: string;
};

type Task = {
  on: boolean;
  name: string;
  date: string;
  responsible: string | number;
  schedule?: Schedule;
};

type TaskGridProps = {
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  users: Array<{ id: string | number; name: string }>;
  isEditing: boolean;
  progress: boolean;
  taskErrors: { responsible: boolean }[];
  setTaskErrors: (errors: { responsible: boolean }[]) => void;
  folhaPagamentoStatus?: string;
  regimeTributario?: string;
  onTaskToggleAttempt?: (taskName: string, checked: boolean) => void;
};

const TaskGrid = ({
  tasks,
  setTasks,
  users,
  isEditing,
  progress,
  taskErrors,
  setTaskErrors,
  folhaPagamentoStatus,
  regimeTributario,
  onTaskToggleAttempt,
}: TaskGridProps) => {
  const fieldRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isTaskDisabled = (taskName: string) => {
    if (!isEditing) return true;

    if (
      folhaPagamentoStatus === "Não Possui" &&
      taskName !== "DET - Monitoramento"
    ) {
      return true;
    }

    if (
      folhaPagamentoStatus === "Com alteração" &&
      taskName === "Folha de Pagamento Sem Alteração"
    ) {
      return true;
    }

    if (
      folhaPagamentoStatus === "Sem alteração" &&
      taskName === "Folha de Pagamento Com Alteração"
    ) {
      return true;
    }

    const apuracaoTasks = [
      "Apuração Fiscal Mensal - Lucro Presumido",
      "Apuração Fiscal Mensal - Lucro Real",
      "Apuração Fiscal Mensal - MEI",
      "Apuração Fiscal Mensal - Simples Nacional",
      "Apuração Fiscal Mensal - Simples Nacional Híbrido",
    ];

    if (apuracaoTasks.includes(taskName)) {
      if (regimeTributario === "PF") {
        return true;
      }

      if (
        regimeTributario === "Lucro Presumido" &&
        taskName !== "Apuração Fiscal Mensal - Lucro Presumido"
      ) {
        return true;
      }

      if (
        regimeTributario === "Lucro Real" &&
        taskName !== "Apuração Fiscal Mensal - Lucro Real"
      ) {
        return true;
      }

      if (
        regimeTributario === "MEI" &&
        taskName !== "Apuração Fiscal Mensal - MEI"
      ) {
        return true;
      }

      if (
        regimeTributario === "Simples Nacional" &&
        taskName !== "Apuração Fiscal Mensal - Simples Nacional"
      ) {
        return true;
      }

      if (
        regimeTributario === "Simples Nacional - Híbrido" &&
        taskName !== "Apuração Fiscal Mensal - Simples Nacional Híbrido"
      ) {
        return true;
      }
    }

    return false;
  };

  const handleTaskToggle = (idx: number, checked: boolean) => {
    if (!isEditing) return;

    const taskName = tasks[idx].name;

    if (onTaskToggleAttempt) {
      onTaskToggleAttempt(taskName, checked);
    }

    const newTasks = [...tasks];
    newTasks[idx].on = checked;
    setTasks(newTasks);
  };

  const handleDateChange = (idx: number, date: string) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    newTasks[idx].date = date;
    setTasks(newTasks);
  };

  const handleResponsibleChange = (
    idx: number,
    responsible: string | number,
  ) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    newTasks[idx].responsible = responsible;
    setTasks(newTasks);

    // Limpa o erro visual ao selecionar
    if (taskErrors[idx]?.responsible) {
      const newErrors = [...taskErrors];
      newErrors[idx] = {
        ...newErrors[idx],
        responsible: false,
      };
      setTaskErrors(newErrors);
    }
  };

  const handleResponsibleFocus = (idx: number) => {
    if (taskErrors[idx]?.responsible) {
      const newErrors = [...taskErrors];
      newErrors[idx] = {
        ...newErrors[idx],
        responsible: false,
      };
      setTaskErrors(newErrors);
    }
  };

  const handleFrequencyChange = (
    idx: number,
    frequency: "daily" | "weekly" | "monthly",
  ) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    if (!newTasks[idx].schedule) {
      newTasks[idx].schedule = {
        frequency,
        dayOfWeek: frequency === "weekly" ? [1] : [],
        monthsInterval: frequency === "monthly" ? 1 : undefined,
      };
    } else {
      newTasks[idx].schedule!.frequency = frequency;
      newTasks[idx].schedule!.dayOfWeek = frequency === "weekly" ? [1] : [];
      if (frequency === "monthly") {
        newTasks[idx].schedule!.monthsInterval =
          newTasks[idx].schedule!.monthsInterval || 1;
      } else {
        newTasks[idx].schedule!.monthsInterval = undefined;
      }
    }
    setTasks(newTasks);
  };

  const handleDayOfWeekToggle = (idx: number, day: number) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    if (!newTasks[idx].schedule) {
      newTasks[idx].schedule = { frequency: "weekly", dayOfWeek: [day] };
    } else {
      const currentDays = newTasks[idx].schedule!.dayOfWeek || [];
      if (currentDays.includes(day)) {
        newTasks[idx].schedule!.dayOfWeek = currentDays.filter(
          (d) => d !== day,
        );
      } else {
        newTasks[idx].schedule!.dayOfWeek = [...currentDays, day];
      }
    }
    setTasks(newTasks);
  };

  const handleDayChange = (idx: number, day: number) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    const padded = String(day).padStart(2, "0");
    newTasks[idx].date = padded;
    setTasks(newTasks);
  };

  const handleMonthsIntervalChange = (idx: number, monthsInterval: number) => {
    if (!isEditing) return;
    const newTasks = [...tasks];
    if (!newTasks[idx].schedule) {
      newTasks[idx].schedule = {
        frequency: "monthly",
        dayOfWeek: [],
        monthsInterval,
      };
    } else {
      newTasks[idx].schedule!.monthsInterval = monthsInterval;
    }
    setTasks(newTasks);
  };

  if (progress) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 4,
          color: "text.secondary",
          fontStyle: "italic",
        }}
      >
        Nenhuma tarefa disponível para esta área
      </Box>
    );
  }

  return (
    <>
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 1 }}>
        <Grid xs={1}>Ativo</Grid>
        <Grid xs={4}>Nome da Tarefa</Grid>
        <Grid xs={2}>Recorrência</Grid>
        <Grid xs={3}>Data de entrega</Grid>
        <Grid xs={2}>Responsável</Grid>
      </Grid>
      {tasks.map((task, idx) => {
        const isDisabled = isTaskDisabled(task.name);

        return (
          <Grid
            container
            spacing={2}
            alignItems="center"
            key={idx}
            sx={{
              mb: 1,
              opacity: isDisabled ? 0.6 : 1,
            }}
            ref={(el) => (fieldRefs.current[idx] = el)}
          >
            <Grid xs={1}>
              <Switch
                checked={task.on}
                disabled={isDisabled}
                onChange={(e) => handleTaskToggle(idx, e.target.checked)}
              />
            </Grid>
            <Grid
              xs={4}
              sx={{ display: "flex", alignItems: "center", minHeight: 40 }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <Box sx={{ flex: 1 }}>{task.name}</Box>
                <Tooltip title="Ainda não temos nada a dizer." placement="top">
                  <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid xs={2}>
              <TextField
                select
                value={task.schedule?.frequency ?? "monthly"}
                size="small"
                fullWidth
                disabled={isDisabled}
                onChange={(e) =>
                  handleFrequencyChange(
                    idx,
                    e.target.value as "daily" | "weekly" | "monthly",
                  )
                }
              >
                <MenuItem value="daily">Diária</MenuItem>
                <MenuItem value="weekly">Semanal</MenuItem>
                <MenuItem value="monthly">Mensal</MenuItem>
              </TextField>
            </Grid>
            <Grid xs={3}>
              {(task.schedule?.frequency ?? "monthly") === "daily" ? (
                <TextField
                  label="Dia"
                  value={task.date || ""}
                  size="small"
                  fullWidth
                  disabled={isDisabled}
                  type="text"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      handleDateChange(idx, "");
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        handleDateChange(idx, String(numValue));
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      handleDateChange(idx, "1");
                    } else {
                      const numValue = parseInt(value);
                      if (isNaN(numValue) || numValue < 1) {
                        handleDateChange(idx, "1");
                      } else if (numValue > 31) {
                        handleDateChange(idx, "31");
                      } else {
                        handleDateChange(idx, String(numValue));
                      }
                    }
                  }}
                />
              ) : (task.schedule?.frequency ?? "monthly") === "weekly" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(5, 1fr)",
                      gap: 0.2,
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    {[1, 2, 3, 4, 5].map((day) => {
                      const dayLabels = ["S", "T", "Q", "Q", "S"];
                      const isSelected =
                        task.schedule?.dayOfWeek?.includes(day) || false;
                      return (
                        <Button
                          key={day}
                          variant={isSelected ? "contained" : "outlined"}
                          size="small"
                          disabled={isDisabled}
                          sx={{
                            minWidth: 0,
                            width: "100%",
                            height: "100%",
                            p: 0,
                            py: 2,
                            fontSize: "14px",
                            borderRadius: 1,
                            aspectRatio: "1",
                          }}
                          onClick={() => handleDayOfWeekToggle(idx, day)}
                        >
                          {dayLabels[day - 1]}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              ) : (task.schedule?.frequency ?? "monthly") === "monthly" ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      label="Intervalo (meses)"
                      value={task.schedule?.monthsInterval || 1}
                      size="small"
                      disabled={isDisabled}
                      type="text"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      sx={{ flex: 2 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          // Não fazer nada quando vazio, deixar o onBlur tratar
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            handleMonthsIntervalChange(idx, numValue);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleMonthsIntervalChange(idx, 1);
                        } else {
                          const numValue = parseInt(value);
                          if (isNaN(numValue) || numValue < 1) {
                            handleMonthsIntervalChange(idx, 1);
                          }
                        }
                      }}
                    />
                    <TextField
                      label="Dia"
                      value={task.date || ""}
                      size="small"
                      disabled={isDisabled}
                      type="text"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      sx={{ flex: 1 }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleDateChange(idx, "");
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue)) {
                            handleDateChange(idx, String(numValue));
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value === "") {
                          handleDateChange(idx, "01");
                        } else {
                          const numValue = parseInt(value);
                          if (isNaN(numValue) || numValue < 1) {
                            handleDateChange(idx, "01");
                          } else if (numValue > 31) {
                            handleDateChange(idx, "31");
                          } else {
                            handleDateChange(idx, String(numValue));
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
              ) : (
                <TextField
                  label="Dia"
                  value={task.date}
                  size="small"
                  fullWidth
                  disabled={isDisabled}
                  type="number"
                  inputProps={{
                    min: 1,
                    max: 31,
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      handleDateChange(idx, "");
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        handleDateChange(
                          idx,
                          String(numValue).padStart(2, "0"),
                        );
                      }
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      handleDateChange(idx, "01");
                    } else {
                      const numValue = parseInt(value);
                      if (isNaN(numValue) || numValue < 1) {
                        handleDateChange(idx, "01");
                      } else if (numValue > 31) {
                        handleDateChange(idx, "31");
                      } else {
                        handleDateChange(
                          idx,
                          String(numValue).padStart(2, "0"),
                        );
                      }
                    }
                  }}
                />
              )}
            </Grid>
            <Grid xs={2}>
              <Tooltip
                title={
                  isEditing && task.on && !task.responsible && !isDisabled
                    ? "Responsável é obrigatório para tarefas ativas"
                    : ""
                }
                placement="top"
              >
                <TextField
                  select
                  value={task.responsible}
                  size="small"
                  fullWidth
                  disabled={isDisabled}
                  onChange={(e) => handleResponsibleChange(idx, e.target.value)}
                  onFocus={() => handleResponsibleFocus(idx)}
                  error={
                    isEditing &&
                    task.on &&
                    taskErrors[idx]?.responsible &&
                    !isDisabled
                  }
                  label={
                    isEditing &&
                    task.on &&
                    taskErrors[idx]?.responsible &&
                    !isDisabled
                      ? "*"
                      : ""
                  }
                  helperText={
                    isEditing &&
                    task.on &&
                    taskErrors[idx]?.responsible &&
                    !isDisabled
                      ? "Responsável é obrigatório"
                      : ""
                  }
                >
                  {Array.isArray(users) && users.length > 0 ? (
                    users
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.name}
                        </MenuItem>
                      ))
                  ) : (
                    <MenuItem disabled value="">
                      Nenhum responsável disponível
                    </MenuItem>
                  )}
                </TextField>
              </Tooltip>
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

export default TaskGrid;
